'use client';

import { memo } from 'react';
import { SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import { useTheme } from '@/components/ui/theme-provider';
import { Link } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar';
import {
  LayoutDashboard,
  FileText,
  Activity,
  Database,
  Shield,
  Zap,
  Settings,
  Moon,
  Sun,
  GitBranch,
  Palette,
} from 'lucide-react';
import getBaseUrl from '@/lib/config';

const menuItems = [
  { title: 'Dashboard', icon: LayoutDashboard, href: '/' },
  { title: 'Resources', icon: Database, href: '/resources' },
  { title: 'Monitoring', icon: Activity, href: '/monitoring' },
  { title: 'Security', icon: Shield, href: '/security' },
  { title: 'Automation', icon: Zap, href: '/automation' },
  { title: 'Workflows', icon: GitBranch, href: '/workflows' },
  { title: 'Costs', icon: FileText, href: '/costs' },
  { title: 'Settings', icon: Settings, href: '/settings' },
  { title: 'Theme Demo', icon: Palette, href: '/theme-demo' },
];

export const AdminSidebar = memo(() => {
  const { theme, setTheme } = useTheme();
  const BASE = getBaseUrl

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="sidebar-brand">
              <Link to="/">
                <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <LayoutDashboard className="h-5 w-5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">OpenConsole</span>
                  <span className="truncate text-xs">Admin Panel</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild className="sidebar-menu-item">
                      <Link to={item.href}>
                        <Icon className="sidebar-menu-icon" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={toggleTheme}
              className="theme-toggle-button"
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? <Sun /> : <Moon />}
              <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {/* Auth Section */}
        <SignedOut>
          <SidebarMenu className="mt-2">
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => window.location.href = `${BASE}/sign-in`}
                className="sidebar-menu-item suffering-effect"
              >
                <img src="/github-mark.svg" alt="GitHub" className="h-5 w-5 mr-2" />
                <span>Sign in with GitHub</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => window.location.href = `${BASE}/sign-in`}
                className="sidebar-menu-item suffering-effect"
              >
                <img src="/google-icon.svg" alt="Google" className="h-5 w-5 mr-2" />
                <span>Sign in with Google</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => window.location.href = `${BASE}/sign-in`}
                className="sidebar-menu-item suffering-effect"
              >
                <img src="/microsoft-icon.svg" alt="Microsoft" className="h-5 w-5 mr-2" />
                <span>Sign in with Microsoft</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SignedOut>
        <SignedIn>
          <div className="flex flex-col items-center mt-2 gap-2">
            <UserButton afterSignOutUrl="/sign-in" appearance={{
              elements: {
                userButtonBox: 'hover:opacity-80 suffering-effect',
                userButtonTrigger: 'rounded-full shadow-md',
              }
            }} />
          </div>
        </SignedIn>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
});

AdminSidebar.displayName = 'AdminSidebar';
